import { IColumn, IconButton, Separator, TooltipHost } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDirectionalHint,
  IPLink,
  IPPersona,
  IPPersonaSize,
  IPPivot,
  IPPivotItem,
  IPSelectionMode,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import HrefLink from 'components/Links/HrefLink';
import BaseTableList from 'components/TableList/BaseTableList';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import SingleTask from 'components/Tasks/SingleTask';
import { SeverityBar } from 'components/Utils/SeverityBar';
import {
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesError,
  restartIcon,
} from 'globalStyles';
import { Integration } from 'models/integrations/integration';
import { IntegrationRun, IntegrationRunStatus } from 'models/integrations/integrationRun';
import Task, { TaskColors, TaskTypes } from 'models/tasks/task';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiReprocessIntegrationRun } from 'services/Api/integrationService';
import { apiRequest } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { toLocaleDateShort, toLocaleDateShortNoYear, toLocaleDateTimeMedium } from 'utils/datetime';
import { getHtmlJSX } from 'utils/html';
import { LocalStorageKeys } from 'utils/localstorage';
import { sortOnDate, sortOnString } from 'utils/sorting';
import { overflow } from 'utils/string';

interface IIntegrationRunPeriodCalloutProps {
  integration: Integration;
  runs: IntegrationRun[];
  onUpdateRun?: (run: IntegrationRun) => void;
  onOpenLatestEvidenceReport?: () => void;
}

export const IntegrationRunPeriodCallout = (props: IIntegrationRunPeriodCalloutProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['integrations', 'translation']);
  const [selectedPivotKey, setSelectedPivotKey] = useState<string>('findings');
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [errors, setErrors] = useState<IntegrationRun[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const nrOfAccepted = useMemo(() => props.runs.filter((r) => r.isAccepted()).length, [props.runs]);
  const nrOfRejected = useMemo(() => props.runs.filter((r) => r.isRejected()).length, [props.runs]);
  const nrOfErrors = useMemo(() => props.runs.filter((r) => r.hasError()).length, [props.runs]);
  const nextRun = useMemo(() => props.runs.find((r) => r.status === IntegrationRunStatus.pending), [props.runs]);

  useEffect(() => {
    const tasks = props.runs.map((r) => r.task).filter((t) => t !== undefined) as Task[];
    const errors = props.runs.filter((r) => r.hasError());

    setTasks(tasks);
    setErrors(errors);

    if (tasks.length === 0 && errors.length > 0) {
      setSelectedPivotKey('errors');
    }
  }, [props.runs]);

  //
  // Task list
  //
  const getColumnsTask = (): IColumn[] => {
    return [
      {
        key: 'period',
        name: t('integrations:AnnualPlan.HoverCard.PivotFindings.Columns.Period'),
        minWidth: 100,
        maxWidth: 100,
      },
      {
        key: 'name',
        name: t('integrations:AnnualPlan.HoverCard.PivotFindings.Columns.Name'),
        minWidth: 200,
        isResizable: true,
        isMultiline: true,
      },
      {
        key: 'status',
        name: t('integrations:AnnualPlan.HoverCard.PivotFindings.Columns.Status'),
        minWidth: 100,
        isResizable: true,
      },
      {
        key: 'deadline',
        name: t('integrations:AnnualPlan.HoverCard.PivotFindings.Columns.Deadline'),
        minWidth: 100,
        isResizable: true,
      },
      {
        key: 'tags',
        name: t('integrations:AnnualPlan.HoverCard.PivotFindings.Columns.Tags'),
        minWidth: 100,
        isResizable: true,
        isMultiline: true,
      },
    ];
  };

  const [columnsTask, setColumnsTask] = useState<IColumn[]>(getColumnsTask());

  const onRenderItemColumnTask = (
    item?: Task,
    column?: IColumn | undefined,
    index?: number | undefined,
  ): JSX.Element | null => {
    if (!item || !column) return null;

    switch (column.key) {
      case 'period':
        const runsForTask = props.runs.filter((r) => r.taskId === item.taskId);
        runsForTask.sort((a, b) => sortOnDate(a.dueAt, b.dueAt));
        if (runsForTask.length === 0) return null;

        const firstTask = runsForTask[0];
        const lastTask = runsForTask[runsForTask.length - 1];
        let periodText = '';
        if (firstTask === lastTask) {
          periodText = toLocaleDateShortNoYear(firstTask.dueAt);
        } else {
          //Format as "1 Jan - 5 Jan"
          periodText = `${toLocaleDateShortNoYear(firstTask.dueAt)} - ${toLocaleDateShortNoYear(lastTask.dueAt)}`;
        }

        return <IPText>{periodText}</IPText>;
      case 'name':
        if (!item) return null;

        const taskUser = item.userId ? appContext.globalDataCache.users.get(item.userId) : undefined;

        return (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPPersona
                imageUrl={taskUser?.getPhotoURLObj()}
                size={IPPersonaSize.size24}
                text={taskUser?.name}
                hidePersonaDetails={true}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPTooltipHost
                content={getHtmlJSX(item.description, true, true) || undefined}
                calloutProps={{ directionalHint: IPDirectionalHint.rightBottomEdge }}
              >
                <IPText>
                  <HrefLink
                    underline
                    href={`${item.taskId}`}
                    onClick={() => {
                      setSelectedTask(item);
                    }}
                  >
                    {overflow(item?.name, 100)}
                  </HrefLink>
                </IPText>
              </IPTooltipHost>
            </IPStack.Item>
          </IPStack>
        );
      case 'tags':
        return <KeyValueTags tags={appContext.globalDataCache.tags.getItemsForId(item?.tagIds)} />;
      case 'status':
        if (!item) return null;
        const status = appContext.globalDataCache.taskStates.get(item.taskStateId);

        return <IPText>{status ? status?.state : ''}</IPText>;
      case 'deadline':
        if (!item) return null;
        if (item.taskType === TaskTypes.Event && item.isSeries()) return null;

        const deadline = item.getDeadline(appContext.globalDataCache);
        let overDeadline = false;
        if (!item.completed) {
          overDeadline = item.isOverDeadline(deadline, new Date());
        }

        return (
          <IPText variant="small" styles={overDeadline ? globalTextStylesError : undefined}>
            {toLocaleDateShort(deadline)}
          </IPText>
        );
    }

    return null;
  };

  const getSortedRunsForTask = (taskId: number): IntegrationRun[] => {
    const runsForTask = props.runs.filter((r) => r.taskId === taskId);
    runsForTask.sort((a, b) => sortOnDate(a.dueAt, b.dueAt));

    return runsForTask;
  };

  const onCompareTask = (a: Task, b: Task, columnKey: string): number => {
    switch (columnKey) {
      case 'period':
        const aRuns = getSortedRunsForTask(a.taskId);
        const bRuns = getSortedRunsForTask(b.taskId);
        const firstARun = aRuns.length > 0 ? aRuns[0] : undefined;
        const firstBRun = bRuns.length > 0 ? bRuns[0] : undefined;

        return sortOnDate(firstARun?.dueAt, firstBRun?.dueAt);
      case 'name':
        return sortOnString(a.name, b.name);
      case 'status':
        const aStatus = appContext.globalDataCache.taskStates.get(a.taskStateId)?.state ?? '';
        const bStatus = appContext.globalDataCache.taskStates.get(b.taskStateId)?.state ?? '';

        return sortOnString(aStatus, bStatus);
      case 'deadline':
        const aDeadline = a.getDeadline(appContext.globalDataCache);
        const bDeadline = b.getDeadline(appContext.globalDataCache);

        return sortOnDate(aDeadline, bDeadline);
      default:
        return 0;
    }
  };

  //
  // Error list
  //
  const getColumnsError = (): IColumn[] => {
    const columns: IColumn[] = [
      {
        key: 'period',
        name: t('integrations:AnnualPlan.HoverCard.PivotErrors.Columns.Period'),
        minWidth: 100,
        maxWidth: 100,
      },
      {
        key: 'name',
        name: t('integrations:AnnualPlan.HoverCard.PivotErrors.Columns.Name'),
        minWidth: 200,
        isResizable: true,
        isMultiline: true,
      },
      {
        key: 'status',
        name: t('integrations:AnnualPlan.HoverCard.PivotErrors.Columns.Status'),
        minWidth: 100,
        isResizable: true,
      },
      {
        key: 'deadline',
        name: t('integrations:AnnualPlan.HoverCard.PivotErrors.Columns.Deadline'),
        minWidth: 100,
        isResizable: true,
      },
    ];

    if (props.onUpdateRun) {
      columns.unshift({
        key: 'reprocess',
        name: '',
        minWidth: 40,
        maxWidth: 40,
        isPadded: false,
        isIconOnly: true,
      });
    }

    return columns;
  };

  const [columnsError, setColumnsError] = useState<IColumn[]>(getColumnsError());

  const onRenderItemColumnError = (
    item?: IntegrationRun,
    column?: IColumn | undefined,
    index?: number | undefined,
  ): JSX.Element | null => {
    if (!item || !column) return null;

    switch (column.key) {
      case 'reprocess':
        return (
          <TooltipHost content={t('integrations:AnnualPlan.HoverCard.PivotErrors.Reprocess')}>
            <IconButton
              disabled={isProcessing || item.status !== IntegrationRunStatus.dead}
              iconProps={restartIcon}
              onClick={() => onReprocessRun(item)}
            />
          </TooltipHost>
        );
      case 'period':
        return <IPText>{toLocaleDateShortNoYear(item.dueAt)}</IPText>;
      case 'name':
        if (item.task) {
          const task = item.task;
          const taskUser = task.userId ? appContext.globalDataCache.users.get(task.userId) : undefined;

          return (
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPPersona
                  imageUrl={taskUser?.getPhotoURLObj()}
                  size={IPPersonaSize.size24}
                  text={taskUser?.name}
                  hidePersonaDetails={true}
                />
              </IPStack.Item>
              <IPStack.Item>
                <IPTooltipHost
                  content={getHtmlJSX(task.description, true, true) || undefined}
                  calloutProps={{ directionalHint: IPDirectionalHint.rightBottomEdge }}
                >
                  <IPText>
                    <HrefLink
                      underline
                      href={`${item.taskId}`}
                      onClick={() => {
                        setSelectedTask(task);
                      }}
                    >
                      {overflow(task?.name, 100)}
                    </HrefLink>
                  </IPText>
                </IPTooltipHost>
              </IPStack.Item>
            </IPStack>
          );
        } else {
          return <IPText>{item.error || 'An unknown error occured. Please contact our ServiceDesk.'}</IPText>;
        }
      case 'status':
        if (!item || !item.task) return null;
        const status = appContext.globalDataCache.taskStates.get(item.task.taskStateId);

        return <IPText>{status ? status?.state : ''}</IPText>;
      case 'deadline':
        if (!item || !item.task) return null;
        if (item.task.taskType === TaskTypes.Event && item.task.isSeries()) return null;

        const deadline = item.task.getDeadline(appContext.globalDataCache);
        let overDeadline = false;
        if (!item.task.completed) {
          overDeadline = item.task.isOverDeadline(deadline, new Date());
        }

        return (
          <IPText variant="small" styles={overDeadline ? globalTextStylesError : undefined}>
            {toLocaleDateShort(deadline)}
          </IPText>
        );
    }

    return null;
  };

  const onCompareError = (a: IntegrationRun, b: IntegrationRun, columnKey: string): number => {
    switch (columnKey) {
      case 'period':
        return sortOnDate(a.dueAt, b.dueAt);
      case 'name':
        if (a.task && b.task) {
          return sortOnString(a.task.name, b.task.name);
        } else if (a.task) {
          return -1;
        } else if (b.task) {
          return 1;
        } else {
          return sortOnString(a.error ?? '', b.error ?? '');
        }
      case 'status':
        if (!a.task || !b.task) return 0;
        const aStatus = appContext.globalDataCache.taskStates.get(a.task.taskStateId)?.state ?? '';
        const bStatus = appContext.globalDataCache.taskStates.get(b.task.taskStateId)?.state ?? '';

        return sortOnString(aStatus, bStatus);
      case 'deadline':
        if (!a.task || !b.task) return 0;
        const aDeadline = a.task.getDeadline(appContext.globalDataCache);
        const bDeadline = b.task.getDeadline(appContext.globalDataCache);

        return sortOnDate(aDeadline, bDeadline);
      default:
        return 0;
    }
  };

  //
  // Run actions
  //
  const onReprocessRun = async (run: IntegrationRun) => {
    try {
      if (!props.onUpdateRun) return;
      setIsProcessing(true);
      props.onUpdateRun(run);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newRun = await apiReprocessIntegrationRun(
        run.integrationRunId,
        props.integration.kpi,
        accessToken,
        appContext.globalDataCache,
      );

      if (!newRun) {
        //run was not found anymore
        appContext.showNotification(
          t('integrations:AnnualPlan.HoverCard.PivotErrors.ReprocessErrorNotFound'),
          false,
          true,
          true,
        );
      } else {
        if (newRun.status !== IntegrationRunStatus.completed && newRun.status !== IntegrationRunStatus.dead) {
          appContext.showNotification(
            t('integrations:AnnualPlan.HoverCard.PivotErrors.ReprocessErrorBusy'),
            false,
            true,
            true,
          );
        } else {
          props.onUpdateRun(newRun);
        }
      }
    } catch (err) {
      logger.debug('onReprocessRun', err);

      let msg: string = '';
      if (AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_EC_AlreadyRunning) {
        msg = t('integrations:AnnualPlan.HoverCard.PivotErrors.ReprocessErrorAlreadyRunning');
      } else if (
        AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_EC_CanOnlyReprocessDeadRuns
      ) {
        msg = t('integrations:AnnualPlan.HoverCard.PivotErrors.ReprocessErrorCanOnlyReprocessDeadRuns');
      }

      if (!msg) {
        const appErr = AppError.fromApiError(err);
        msg = t('integrations:AnnualPlan.HoverCard.PivotErrors.ReprocessError', { error: appErr.message });
      }

      appContext.showNotification(msg, true, false, true);
    } finally {
      setIsProcessing(false);
    }
  };

  //
  // Task handlers
  //
  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      const newTasks = tasks.map((t) => (t.taskId === task.taskId ? task : t));
      setTasks(newTasks);
    }
    setSelectedTask(undefined);
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
  };

  const updateTask = async (task: Task) => {};

  //
  // Styles
  //
  const getBackgroundColor = (hasNotAccepted: boolean): string => {
    let backgroundColor;

    if (hasNotAccepted) {
      backgroundColor = appContext.useDarkMode ? TaskColors.DarkOverDeadline : TaskColors.OverDeadline;
    } else {
      backgroundColor = appContext.useDarkMode ? TaskColors.DarkCompleted : TaskColors.Completed;
    }

    return backgroundColor;
  };

  //
  // Main render
  //
  const hasFindingsOrErrors = nrOfRejected > 0 || nrOfErrors > 0;

  return (
    <IPStack>
      <IPStack
        tokens={globalStackTokensGapSmall}
        styles={{ root: { width: hasFindingsOrErrors ? 'min(95vw, 800px)' : undefined, padding: 20 } }}
      >
        <IPStack.Item>
          <IPStack horizontal tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                <SeverityBar
                  content={nrOfAccepted}
                  color={getBackgroundColor(false)}
                  label={t('integrations:AnnualPlan.HoverCard.Accepted')}
                />
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                <SeverityBar
                  content={nrOfRejected}
                  color={getBackgroundColor(nrOfRejected !== 0)}
                  label={t('integrations:AnnualPlan.HoverCard.Findings')}
                />
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack tokens={globalStackTokensGapSmall}>
                <SeverityBar
                  content={nrOfErrors}
                  color={getBackgroundColor(nrOfErrors > 0)}
                  label={t('integrations:AnnualPlan.HoverCard.Errors')}
                />
              </IPStack>
            </IPStack.Item>
            {props.onOpenLatestEvidenceReport && (
              <IPStack.Item styles={{ root: { alignItems: 'center', display: 'flex' } }}>
                <IPLink onClick={props.onOpenLatestEvidenceReport} underline>
                  {t('integrations:AnnualPlan.HoverCard.EvidenceReportLink')}
                </IPLink>
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPText>
            {t('integrations:DefProps.NextRunText', {
              date: nextRun?.dueAt
                ? toLocaleDateTimeMedium(nextRun?.dueAt)
                : t('integrations:DefProps.NextRunTextNotScheduled'),
            })}
          </IPText>
        </IPStack.Item>
        {hasFindingsOrErrors && (
          <IPStack styles={{ root: { height: 350, maxHeight: 350 } }}>
            <IPStack.Item>
              <Separator styles={{ root: { height: 1 } }} />
            </IPStack.Item>
            <IPStack.Item>
              <IPPivot
                selectedKey={selectedPivotKey}
                onLinkClick={(item) => setSelectedPivotKey(item?.props.itemKey ?? '')}
                overflowBehavior="none"
                headersOnly={true}
              >
                <IPPivotItem
                  key="findings"
                  itemKey="findings"
                  headerText={t('integrations:AnnualPlan.HoverCard.Pivot.Findings')}
                ></IPPivotItem>
                <IPPivotItem
                  key="errors"
                  itemKey="errors"
                  headerText={t('integrations:AnnualPlan.HoverCard.Pivot.Errors')}
                />
              </IPPivot>
            </IPStack.Item>
            {/* Keep the pivot item surfaces equal size. Otherwise the callout would resize and a MouseLeave event could dismis it.*/}
            {selectedPivotKey === 'findings' && (
              <IPStack.Item grow styles={{ root: { overflowY: 'scroll' } }}>
                <BaseTableList
                  compact
                  items={tasks}
                  selectionMode={IPSelectionMode.none}
                  columns={columnsTask}
                  setColumns={setColumnsTask}
                  compare={onCompareTask}
                  onRenderCol={onRenderItemColumnTask}
                  cacheKeySort={LocalStorageKeys.TaskScheduleRunSortTask}
                />
              </IPStack.Item>
            )}
            {selectedPivotKey === 'errors' && (
              <IPStack.Item grow styles={{ root: { overflowY: 'scroll' } }}>
                <BaseTableList
                  compact
                  items={errors}
                  selectionMode={IPSelectionMode.none}
                  columns={columnsError}
                  setColumns={setColumnsError}
                  compare={onCompareError}
                  onRenderCol={onRenderItemColumnError}
                  cacheKeySort={LocalStorageKeys.TaskScheduleRunSortError}
                />
              </IPStack.Item>
            )}
          </IPStack>
        )}
      </IPStack>
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};
